<template>
    <div>
        <div class="flex items-center gap-4 mb-3 flex-wrap">
            <span class="text-sm text-gray-400">Group by</span>
            <SelectButton v-model="groupByChoice" :options="groupOptions" optionLabel="label" optionValue="value" />
            <span class="text-xs text-gray-500 ml-auto">
                {{ rows.length }} row{{ rows.length === 1 ? '' : 's' }}
                <template v-if="rows.length === 500"> (capped)</template>
            </span>
        </div>

        <ProgressSpinner v-if="loading" style="width: 40px; height: 40px" />

        <div v-else-if="!rows.length" class="text-center text-sm text-gray-400 py-10 border border-dashed border-gray-700 rounded">
            No postbacks with <code>params.param1</code> in this date range.
        </div>

        <DataTable v-else :value="rows" :scrollable="true" scrollHeight="72vh" sortMode="single"
            :sortField="'total'" :sortOrder="-1" class="text-sm">
            <Column v-for="key in currentGroupBy" :key="key" :field="key" :header="prettyLabel(key)" sortable :headerStyle="headerStyles">
                <template #body="{ data }">
                    <code v-if="data[key] !== undefined && data[key] !== ''">{{ data[key] }}</code>
                    <span v-else class="text-gray-500 italic">(empty)</span>
                </template>
            </Column>
            <Column field="regs" header="Regs" sortable :headerStyle="headerStyles" style="width: 6rem">
                <template #body="{ data }">
                    <span :class="{ 'text-blue-300': data.regs > 0 }">{{ data.regs }}</span>
                </template>
            </Column>
            <Column field="ftds" header="FTDs" sortable :headerStyle="headerStyles" style="width: 6rem">
                <template #body="{ data }">
                    <span :class="{ 'text-green-300': data.ftds > 0 }">{{ data.ftds }}</span>
                </template>
            </Column>
            <Column field="total" header="Total" sortable :headerStyle="headerStyles" style="width: 6rem" />
            <Column field="firstSeen" header="First seen" sortable :headerStyle="headerStyles">
                <template #body="{ data }">{{ formatDate(data.firstSeen) }}</template>
            </Column>
            <Column field="lastSeen" header="Last seen" sortable :headerStyle="headerStyles">
                <template #body="{ data }">{{ formatDate(data.lastSeen) }}</template>
            </Column>
            <Column header="" :headerStyle="headerStyles" style="width: 5rem">
                <template #body="{ data }">
                    <Button icon="pi pi-arrow-right" text rounded size="small"
                        v-tooltip.left="'Drill into the Postback Log with these params applied'"
                        @click="drill(data)" />
                </template>
            </Column>
        </DataTable>
    </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import SelectButton from 'primevue/selectbutton';
import Button from 'primevue/button';
import ProgressSpinner from 'primevue/progressspinner';
import PostbackService from '@/services/PostbackService';
import { headerStyles } from '@/global/headerStyles';

const props = defineProps({
    dateRange: { type: Array, required: true },
});

const route = useRoute();
const router = useRouter();

// param3+ intentionally not offered — no operator currently sends more than
// two segments after the campaign key, so adding a third group-by button just
// produced an all-"(empty)" bucket. Re-add (or better, make data-driven off
// the API's observed max param arity) if that changes.
const groupOptions = [
    { label: 'param1',          value: 'param1' },
    { label: 'param1 + param2', value: 'param1,param2' },
];
const groupByChoice = ref(route.query.groupBy || 'param1,param2');
const currentGroupBy = computed(() => groupByChoice.value.split(','));

const rows = ref([]);
const loading = ref(false);

const prettyLabel = key => key === 'param1' ? 'Param 1'
    : key === 'param2' ? 'Param 2'
    : key === 'param3' ? 'Param 3'
    : key;

const formatDate = d => d ? new Date(d).toISOString().slice(0, 16).replace('T', ' ') + 'Z' : '';

async function load() {
    if (!props.dateRange || props.dateRange.length !== 2 || !props.dateRange[0] || !props.dateRange[1]) return;
    loading.value = true;
    try {
        const response = await PostbackService.getParamsBreakdown({
            from: props.dateRange[0].toISOString(),
            to:   props.dateRange[1].toISOString(),
            groupBy: currentGroupBy.value,
        });
        rows.value = response.data?.rows || [];
    } catch (err) {
        console.error('Error fetching params breakdown:', err);
        rows.value = [];
    } finally {
        loading.value = false;
    }
}

// Sync groupBy to the URL so links / refresh preserve the choice.
watch(groupByChoice, (v) => {
    const query = { ...route.query, groupBy: v };
    router.replace({ query });
    load();
});

watch(() => props.dateRange, load, { deep: true });
onMounted(load);

/**
 * Drill target: same page, view=log, plus ?param1=x&param2=y so the log
 * filters client-side to just those postbacks. See PostbackLog.vue for the
 * pickup of these params.
 */
function drill(row) {
    const query = { ...route.query, view: 'log' };
    for (const key of currentGroupBy.value) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
            query[key] = row[key];
        } else {
            delete query[key];
        }
    }
    delete query.groupBy;
    router.push({ query });
}
</script>
